import React, { memo, useCallback, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import classNames from 'classnames'
import { useTranslation, Trans } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Grid, Theme } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import InfiniteScroll from 'react-infinite-scroller'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import { useLocation } from 'react-router-dom'

import { CardImage, Loading } from 'components'
import styles from '../../styles'
import { SearchEmptyResult } from '..'
import { TSearchCompaniesProps } from 'models'
import { StoreValue, FeedCompaniesQuery } from 'store'
import { ConditionalWrapper } from 'utils'
import CompanySearchSection from './companySearchSection/companySearchSection'

const useStyles = makeStyles(styles)

const Companies: React.FunctionComponent<TSearchCompaniesProps> = ({
	showCompanies = true,
	storeSearchDebounceQuery,
	storeSearchLoading,
}) => {
	const theme: Theme = useTheme()
	const isMdDown: boolean = useMediaQuery(theme.breakpoints.down('md'))
	const classes = useStyles()
	const { t } = useTranslation()
	const [isFilterVisible, setIsFilterVisible] = useState(false)
	// Employers filters
	const [locationsIds, setLocationsIds] = useState<string[]>([])
	const [industryIds, setIndustryIds] = useState(undefined)
	const [types, setTypes] = useState([])
	const [sizes, setSizes] = useState([])
	const { state }: { state: { seeEmployersAndTeamsSection?: boolean } } =
		useLocation()

	const {
		data: feedCompaniesData,
		loading: feedCompaniesLoading,
		fetchMore: fetchFeedCompanies,
	} = useQuery(FeedCompaniesQuery, {
		fetchPolicy: 'cache-first',
		variables: {
			data: {
				industryIds,
				name: storeSearchDebounceQuery,
				types,
				first: 5,
				sizes,
				locationsIds,
				after: '',
			},
		},
	})

	const handleSetLocationsIds = useCallback(
		(locationsIds: string[]) => {
			setLocationsIds(locationsIds)
		},
		[setLocationsIds],
	)

	const handleSetIndustryIds = useCallback(
		industryIds => {
			if (industryIds && industryIds.length > 0) {
				setIndustryIds(industryIds)
			} else {
				setIndustryIds(undefined)
			}
		},
		[setIndustryIds],
	)

	const handleSetTypes = useCallback(
		types => {
			setTypes(types)
		},
		[setTypes],
	)
	const handleSetSizes = useCallback(
		sizes => {
			setSizes(sizes)
		},
		[setSizes],
	)

	const showFilter = useCallback(() => {
		setIsFilterVisible(true)
	}, [])

	const closeFilter = useCallback(() => {
		// (event: React.MouseEvent | React.ChangeEvent<any>) => {
		//  if (event && menuRef && menuRef.current) {
		//    if (menuRef.current.contains(event.target)) return null
		//  }
		setIsFilterVisible(false)
	}, [])

	const toggleDrawer = useCallback(
		(open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event &&
				event.type === 'keydown' &&
				((event as React.KeyboardEvent).key === 'Tab' ||
					(event as React.KeyboardEvent).key === 'Shift')
			) {
				return undefined
			}
			setIsFilterVisible(open)
		},
		[],
	)

	const companies = feedCompaniesData
		? feedCompaniesData &&
		  feedCompaniesData.feedCompanies &&
		  feedCompaniesData.feedCompanies.edges.map((edge: any) => edge.node)
		: []

	const hasNextPage = feedCompaniesData
		? feedCompaniesData &&
		  feedCompaniesData.feedCompanies &&
		  feedCompaniesData.feedCompanies.pageInfo.hasNextPage
		: false

	return (
		<>
			<ConditionalWrapper
				condition={showCompanies}
				wrapper={(children: any) => (
					<Grid
						className={classes.companiesContainer}
						container={true}
						direction="row"
					>
						<div
							className={classNames([
								classes.pageTitle,
								{
									[classes.titleWithFilterButton]:
										!state.seeEmployersAndTeamsSection,
									[classes.onlyFilterButton]:
										state.seeEmployersAndTeamsSection,
								},
							])}
						>
							<div>
								{state && !state.seeEmployersAndTeamsSection && (
									<Trans key="exploreCompanies">
										Explore&nbsp;
										<span className={classes.pageTitleMain}>
											Companies
										</span>
									</Trans>
								)}
							</div>
							<Hidden only={['lg', 'xl']}>
								<div>
									<Button
										classes={{
											root: classes.filterSectionButton,
										}}
										variant="contained"
										color="primary"
										onClick={toggleDrawer(true)}
									>
										{t('Filter')}
									</Button>
								</div>
							</Hidden>
						</div>

						<Grid
							className={classes.companiesMainContent}
							container={true}
							direction="column"
						>
							{children}{' '}
						</Grid>
					</Grid>
				)}
			>
				{!feedCompaniesLoading &&
					feedCompaniesData &&
					feedCompaniesData.feedCompanies &&
					feedCompaniesData.feedCompanies.edges.length === 0 && (
						<SearchEmptyResult query={storeSearchDebounceQuery} />
					)}
				{!storeSearchLoading ? (
					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerCompany,
							classes.fixFlexList,
						)}
					>
						<InfiniteScroll
							pageStart={0}
							loadMore={() =>
								fetchFeedCompanies({
									variables: {
										data: {
											after: feedCompaniesData
												.feedCompanies.edges[
												feedCompaniesData.feedCompanies
													.edges.length - 1
											].cursor,
											industryIds,
											name: storeSearchDebounceQuery,
											types,
											first: 5,
											sizes,
											locationsIds,
										},
									},
									updateQuery: (
										previousQueryResult: any,
										{
											fetchMoreResult: {
												feedCompanies: {
													edges: newEdges,
													pageInfo,
												},
											},
										}: any,
									) => {
										return newEdges.length > 0
											? {
													// Put the new companies at the end of the list and update `pageInfo`
													feedCompanies: {
														__typename:
															previousQueryResult
																.feedCompanies
																.__typename,
														edges: [
															...previousQueryResult
																.feedCompanies
																.edges,
															...newEdges,
														],
														pageInfo,
													},
											  }
											: previousQueryResult
									},
								})
							}
							hasMore={hasNextPage}
							loader={
								<div
									style={{ flexGrow: 1, display: 'flex' }}
									key={'loading'}
								>
									<Loading
										size={48}
										style={{ margin: '50px auto' }}
									/>
								</div>
							}
							className={classes.searchContentCards}
						>
							{companies &&
								companies.map((item: any, index: number) => (
									<CardImage
										company={item}
										key={`company_${index}`}
									/>
								))}
						</InfiniteScroll>
					</div>
				) : (
					<div
						style={{ flexGrow: 1, display: 'flex', marginTop: 110 }}
					>
						<Loading size={58} />
					</div>
				)}
			</ConditionalWrapper>
			{
				<Hidden mdDown>
					<div className={classes.filterSection}>
						<CompanySearchSection
							handleSetTypes={handleSetTypes}
							handleSetSizes={handleSetSizes}
							handleSetIndustryIds={handleSetIndustryIds}
							handleSetLocationsIds={handleSetLocationsIds}
						/>
					</div>
				</Hidden>
			}
			{isMdDown && (
				<>
					<SwipeableDrawer
						anchor="right"
						open={isFilterVisible}
						onOpen={showFilter}
						onClose={closeFilter}
						classes={{
							paper: classes.filterDrawer,
						}}
					>
						<div className={classes.companiesSearchContainer}>
							<CompanySearchSection
								handleSetTypes={handleSetTypes}
								handleSetSizes={handleSetSizes}
								handleSetIndustryIds={handleSetIndustryIds}
								handleSetLocationsIds={handleSetLocationsIds}
								toggleDrawer={toggleDrawer}
							/>
						</div>
					</SwipeableDrawer>
				</>
			)}
		</>
	)
}

export default memo((props: TSearchCompaniesProps) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'search.loading|storeSearchLoading',
			'search.debounceQuery|storeSearchDebounceQuery',
		]}
	>
		<Companies {...props} />
	</StoreValue>
))
